import type { CreateProductLineBody, ProductCenterProductRow, ProductLine } from "@adpilot/shared";
import { IconPlus } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SearchInput } from "@/components/patterns/SearchInput";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { MAX_PRODUCT_LINE_ASINS, parseAsinList } from "./product-line-asins";

export interface CreationShopProps {
  creationShops?: Array<{ id: string; name: string }>;
  creationShopId?: string;
  onCreationShopChange?: (id: string) => void;
}

export function CreateProductLineDialog({
  creationShops,
  creationShopId,
  onCreationShopChange,
  rows,
  isLoadingRows,
  isRowsError,
  hasMoreRows,
  onSearchRows,
  onLoadMoreRows,
  onRetryRows,
  onOpenChange,
  onCreate,
  isCreating,
}: {
  rows: ProductCenterProductRow[];
  isLoadingRows: boolean;
  isRowsError: boolean;
  hasMoreRows: boolean;
  onSearchRows: (search: string) => void;
  onLoadMoreRows: () => void;
  onRetryRows: () => void;
  onOpenChange: (open: boolean) => void;
  onCreate: (input: CreateProductLineBody) => Promise<ProductLine>;
  isCreating: boolean;
} & CreationShopProps): React.ReactElement {
  const { t } = useTranslation("productCenter");
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [search, setSearch] = useState("");
  const [pastedAsins, setPastedAsins] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);
  useEffect(() => {
    // A scope change must never carry selected ASINs into another shop's write.
    if (creationShopId === undefined) return;
    setSelected([]);
    setPastedAsins("");
    setSearch("");
    setSubmitted(false);
    onSearchRows("");
  }, [creationShopId, onSearchRows]);
  const asinOptions = useMemo(() => {
    const unique = new Map<string, { title: string; assignedLine: string | null }>();
    for (const row of rows) {
      if (!row.identity.asin) continue;
      const current = unique.get(row.identity.asin);
      unique.set(row.identity.asin, {
        title: current?.title ?? row.identity.title ?? row.identity.asin,
        assignedLine: current?.assignedLine ?? row.productLine?.name ?? null,
      });
    }
    const term = search.trim().toLowerCase();
    return Array.from(unique, ([asin, value]) => ({ asin, ...value })).filter(
      (option) =>
        !term ||
        option.asin.toLowerCase().includes(term) ||
        option.title.toLowerCase().includes(term),
    );
  }, [rows, search]);
  const parsedAsins = useMemo(() => parseAsinList(pastedAsins), [pastedAsins]);
  const submittedAsins = useMemo(
    () => [...new Set([...selected, ...parsedAsins.valid])],
    [parsedAsins.valid, selected],
  );
  const nameInvalid = submitted && name.trim().length === 0;
  const asinInvalid = submitted && submittedAsins.length === 0;
  const pastedAsinsInvalid = submitted && parsedAsins.invalid.length > 0;
  const tooManyAsins = submittedAsins.length > MAX_PRODUCT_LINE_ASINS;

  function reset(): void {
    setName("");
    setSearch("");
    onSearchRows("");
    setPastedAsins("");
    setSelected([]);
    setSubmitted(false);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        onOpenChange(next);
        if (!next) reset();
      }}
    >
      <DialogTrigger
        render={<Button size="sm" disabled={creationShops != null && !creationShopId} />}
      >
        <IconPlus data-icon="inline-start" aria-hidden />
        {t("createProductLine")}
      </DialogTrigger>
      <DialogContent className="max-w-[620px]">
        <form
          className="flex min-h-0 flex-col"
          onSubmit={async (event) => {
            event.preventDefault();
            setSubmitted(true);
            if (
              (creationShops != null && !creationShopId) ||
              !name.trim() ||
              submittedAsins.length === 0 ||
              parsedAsins.invalid.length > 0 ||
              tooManyAsins
            )
              return;
            try {
              await onCreate({
                name: name.trim(),
                selection: { dimension: "ASIN", keys: submittedAsins },
              });
              setOpen(false);
              onOpenChange(false);
              reset();
            } catch {
              // Mutation feedback is displayed by the route; keep the dialog open for correction.
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>{t("productLineDialog.title")}</DialogTitle>
            <DialogDescription>{t("productLineDialog.description")}</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto px-6 pb-5">
            <FieldGroup>
              {creationShops && onCreationShopChange ? (
                <Field>
                  <FieldLabel>{t("scope.targetShop")}</FieldLabel>
                  <Select
                    value={creationShopId}
                    onValueChange={(value) => {
                      if (value) onCreationShopChange(value);
                    }}
                    disabled={isCreating}
                    items={creationShops.map((shop) => ({ value: shop.id, label: shop.name }))}
                  >
                    <SelectTrigger aria-label={t("scope.targetShop")}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {creationShops.map((shop) => (
                        <SelectItem key={shop.id} value={shop.id}>
                          {shop.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FieldDescription>{t("scope.targetShopDescription")}</FieldDescription>
                </Field>
              ) : null}
              <Field data-invalid={nameInvalid || undefined}>
                <FieldLabel htmlFor="product-line-name">
                  {t("productLineDialog.nameLabel")}
                </FieldLabel>
                <Input
                  id="product-line-name"
                  value={name}
                  onChange={(event) => setName(event.currentTarget.value)}
                  placeholder={t("productLineDialog.namePlaceholder")}
                  aria-invalid={nameInvalid || undefined}
                  maxLength={100}
                />
                {nameInvalid ? (
                  <FieldError>{t("productLineDialog.nameRequired")}</FieldError>
                ) : null}
              </Field>
              <Field
                data-invalid={asinInvalid || pastedAsinsInvalid || tooManyAsins ? true : undefined}
              >
                <FieldLabel>{t("productLineDialog.asinLabel")}</FieldLabel>
                <FieldDescription>
                  {t("productLineDialog.asinDescription")} ·{" "}
                  {t("productLineDialog.selected", { count: submittedAsins.length })}
                </FieldDescription>
                <SearchInput
                  showIcon
                  className="w-full"
                  value={search}
                  onChange={(event) => {
                    const next = event.currentTarget.value;
                    setSearch(next);
                    onSearchRows(next);
                  }}
                  placeholder={t("productLineDialog.asinSearchPlaceholder")}
                />
                <div className="flex max-h-52 w-full flex-col overflow-y-auto rounded-[10px] border border-line">
                  {isRowsError ? (
                    <div className="flex min-h-32 items-center justify-center px-3">
                      <ErrorState
                        message={t("productLineDialog.loadError")}
                        onRetry={onRetryRows}
                      />
                    </div>
                  ) : asinOptions.length ? (
                    asinOptions.map((option) => {
                      const checked = selected.includes(option.asin);
                      const disabled =
                        option.assignedLine !== null ||
                        (!checked && submittedAsins.length >= MAX_PRODUCT_LINE_ASINS);
                      return (
                        <div
                          key={option.asin}
                          className={cn(
                            "flex min-h-12 items-center gap-3 border-b border-line px-3 py-2 last:border-b-0",
                            disabled ? "bg-subtle/60 opacity-60" : "cursor-pointer hover:bg-subtle",
                          )}
                        >
                          <Checkbox
                            id={`product-line-asin-${option.asin}`}
                            checked={checked}
                            disabled={disabled}
                            onCheckedChange={(next) =>
                              setSelected((current) =>
                                next
                                  ? current.length < MAX_PRODUCT_LINE_ASINS
                                    ? [...current, option.asin]
                                    : current
                                  : current.filter((asin) => asin !== option.asin),
                              )
                            }
                            aria-label={option.asin}
                          />
                          <label
                            htmlFor={`product-line-asin-${option.asin}`}
                            className={cn(
                              "flex min-w-0 flex-1 flex-col gap-0.5",
                              disabled ? "cursor-not-allowed" : "cursor-pointer",
                            )}
                          >
                            <span className="truncate text-xs font-medium text-ink">
                              {option.title}
                            </span>
                            <span className="font-mono text-[10px] text-muted-foreground">
                              {option.asin}
                              {option.assignedLine
                                ? ` · ${t("productLineDialog.assignedTo", { name: option.assignedLine })}`
                                : ""}
                            </span>
                          </label>
                        </div>
                      );
                    })
                  ) : (
                    <p className="px-3 py-8 text-center text-xs text-muted-foreground">
                      {isLoadingRows
                        ? t("productLineDialog.loadingAsins")
                        : t("productLineDialog.noAsins")}
                    </p>
                  )}
                  {hasMoreRows ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="xs"
                      className="m-2"
                      disabled={isLoadingRows}
                      onClick={onLoadMoreRows}
                    >
                      {isLoadingRows
                        ? t("productLineDialog.loadingAsins")
                        : t("productLineDialog.loadMoreAsins")}
                    </Button>
                  ) : null}
                </div>
                <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
                  <span className="h-px flex-1 bg-line" />
                  <span>{t("productLineDialog.orPaste")}</span>
                  <span className="h-px flex-1 bg-line" />
                </div>
                <Textarea
                  value={pastedAsins}
                  onChange={(event) => setPastedAsins(event.currentTarget.value)}
                  placeholder={t("productLineDialog.pastePlaceholder")}
                  aria-label={t("productLineDialog.pasteLabel")}
                  aria-invalid={pastedAsinsInvalid || undefined}
                  className="min-h-20 font-mono text-xs"
                />
                {pastedAsinsInvalid ? (
                  <FieldError>
                    {t("productLineDialog.invalidAsins", {
                      values: parsedAsins.invalid.join(", "),
                    })}
                  </FieldError>
                ) : null}
                {tooManyAsins ? (
                  <FieldError>
                    {t("productLineDialog.tooManyAsins", { count: MAX_PRODUCT_LINE_ASINS })}
                  </FieldError>
                ) : null}
                {asinInvalid ? (
                  <FieldError>{t("productLineDialog.asinRequired")}</FieldError>
                ) : null}
              </Field>
            </FieldGroup>
          </div>
          <DialogFooter>
            <DialogClose render={<Button type="button" variant="outline" />}>
              {t("productLineDialog.cancel")}
            </DialogClose>
            <Button type="submit" disabled={isCreating}>
              {isCreating ? t("productLineDialog.creating") : t("productLineDialog.create")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
